import { useEnvContext } from "context/EnvContext";
import { REHAB_FORM_NAMES } from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { getStaticAsset } from "core/model/config";
import { RehabFormName, RehabForms } from "core/types";
import { getOntology } from "dsl/ecosystems/AcpTranslations/utils";
import { getRehabFormConfig } from "dsl/ecosystems/PatientForms/RehabForms/configs";
import { RehabFormDataMapped } from "dsl/ecosystems/PatientForms/RehabForms/utils";
import { createAHBRequestXml } from "onprem/utils";
import { PDFDocument } from "pdf-lib";
import { useCallback } from "react";
import { transformXmlToPdf } from "./useAHBResponseToPDF";

export type CreateRehaPdfProps = {
  formData: RehabFormDataMapped<RehabFormName>;
  formName: RehabFormName;
  forms: RehabForms | undefined;
};

const formPageRanges: Partial<
  Record<RehabFormName, { endPage: number; startPage: number }>
> = {
  [REHAB_FORM_NAMES.GKV_GENERAL]: { startPage: 1, endPage: 4 },
  [REHAB_FORM_NAMES.GKV_MEDICAL]: { startPage: 5, endPage: 10 },
};

export const useCreateRehaPdf = () => {
  const { env } = useEnvContext();
  const { currentLocale: locale } = useDateLocale();

  const createRehaPdf = useCallback(
    async ({ formData, formName, forms }: CreateRehaPdfProps) => {
      const { pdfConfig } = getRehabFormConfig(formName);
      if (pdfConfig.pdfFiller) {
        const formUrl = getStaticAsset(pdfConfig.blankPdfAssetPath);
        const formPdfBytes = await fetch(formUrl).then((res) =>
          res.arrayBuffer(),
        );
        const pdfDoc = await PDFDocument.load(formPdfBytes);
        const form = pdfDoc.getForm();

        pdfConfig.pdfFiller({ formData, form, locale });

        const pdfBytes = await pdfDoc.save();
        return pdfBytes;
      } else {
        const { xml } = await createAHBRequestXml({ env, forms, formName });
        const xslUrl = getStaticAsset("Stylesheet_GKHAR_1.1.0.xsl");
        const res = await fetch(xslUrl);
        if (!res.ok) throw new Error("Failed to load XSL");
        const xsl = await res.text();

        return transformXmlToPdf({
          xml,
          xsl,
          ...formPageRanges[formName],
        });
      }
    },
    [locale, getOntology],
  );

  return createRehaPdf;
};
